import React, { useMemo } from 'react';
import { MessageCircle, User } from 'lucide-react';
import { Segmented, EmptyState } from '../dash/Toolbar';
import { Tag } from '../dash/ListRows';
import { ago, series } from '../dash/palette';
import { cn } from '../../../lib/utils';
import { ChatSession, StatusFilter, displayName } from './types';

const initials = (name: string) =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? '')
    .join('');

/** One-line preview: drop markdown bullets, bold markers and link syntax. */
const plainPreview = (text: string) =>
  text
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
    .replace(/\*\*|__|`/g, '')
    .replace(/^\s*(?:[-*•]|\d+\.)\s+/gm, '')
    .replace(/\s+/g, ' ')
    .trim();

export const statusTag = (status: string) => {
  if (status === 'waiting_for_admin') return <Tag color={series.amber}>Waiting</Tag>;
  if (status === 'admin_joined') return <Tag color={series.greenDark}>With team</Tag>;
  return <Tag color={series.sky}>AI</Tag>;
};

const matchesFilter = (session: ChatSession, filter: StatusFilter) => {
  if (filter === 'all') return true;
  if (filter === 'waiting') return session.status === 'waiting_for_admin';
  if (filter === 'admin') return session.status === 'admin_joined';
  return session.status !== 'waiting_for_admin' && session.status !== 'admin_joined';
};

const matchesSearch = (session: ChatSession, term: string) => {
  if (!term) return true;
  const q = term.toLowerCase();
  return (
    (session.visitor_name || '').toLowerCase().includes(q) ||
    (session.visitor_email || '').toLowerCase().includes(q) ||
    (session.last_message || '').toLowerCase().includes(q)
  );
};

export const SessionList: React.FC<{
  sessions: ChatSession[];
  search: string;
  statusFilter: StatusFilter;
  onStatusFilterChange: (f: StatusFilter) => void;
  selectedId: string | null;
  onSelect: (session: ChatSession) => void;
}> = ({ sessions, search, statusFilter, onStatusFilterChange, selectedId, onSelect }) => {
  const counts = useMemo(() => {
    const waiting = sessions.filter((s) => s.status === 'waiting_for_admin').length;
    const admin = sessions.filter((s) => s.status === 'admin_joined').length;
    return { all: sessions.length, waiting, admin, ai: sessions.length - waiting - admin };
  }, [sessions]);

  const filtered = sessions.filter((s) => matchesFilter(s, statusFilter) && matchesSearch(s, search));

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="shrink-0 border-b border-[color:var(--oa-line-2)] p-2">
        <Segmented
          value={statusFilter}
          onChange={onStatusFilterChange}
          className="w-full"
          options={[
            { value: 'all', label: 'All', count: counts.all },
            { value: 'waiting', label: 'Waiting', count: counts.waiting },
            { value: 'admin', label: 'With team', count: counts.admin },
            { value: 'ai', label: 'AI', count: counts.ai },
          ]}
        />
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto">
        {filtered.length === 0 ? (
          <EmptyState icon={MessageCircle} title="No open conversations" hint={search ? 'No results match your search.' : undefined} />
        ) : (
          <ul>
            {filtered.map((session, i) => {
              const selected = session.id === selectedId;
              const waiting = session.status === 'waiting_for_admin';
              const name = displayName(session);
              const ini = initials(name);
              return (
                <li key={session.id} style={{ animation: 'oa-rowin .5s var(--oa-ease) both', animationDelay: `${Math.min(i, 14) * 45}ms` }}>
                  <button
                    type="button"
                    onClick={() => onSelect(session)}
                    className={cn(
                      'flex w-full items-start gap-2.5 border-b border-[color:var(--oa-line-2)] border-l-2 px-3 py-2.5 text-left transition-colors',
                      selected ? 'border-l-[color:var(--oa-accent)] bg-[color:var(--oa-raised-2)]' : waiting ? 'border-l-[color:var(--oa-warn)] hover:bg-[color:var(--oa-raised)]' : 'border-l-transparent hover:bg-[color:var(--oa-raised)]'
                    )}
                  >
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[color:var(--oa-raised-2)] text-[12px] font-semibold text-[color:var(--oa-text)]">
                      {ini || <User className="h-4 w-4 text-[color:var(--oa-muted)]" />}
                    </span>

                    <span className="min-w-0 flex-1">
                      <span className="flex items-center justify-between gap-2">
                        <span className="truncate text-[13px] font-medium text-[color:var(--oa-text)]">{name}</span>
                        <span className="shrink-0 text-[11.5px] tabular-nums text-[color:var(--oa-muted)]">{session.last_message_at ? ago(session.last_message_at) : 'New'}</span>
                      </span>
                      <span className="mt-0.5 flex items-center justify-between gap-2">
                        <span className="truncate text-[12.5px] text-[color:var(--oa-muted)]">{session.last_message ? plainPreview(session.last_message) : '—'}</span>
                        {session.unread_count > 0 ? (
                          <span className="ml-1 flex h-[18px] min-w-[18px] shrink-0 items-center justify-center rounded-full bg-[color:var(--oa-accent)] px-1 text-[10.5px] font-semibold text-[color:var(--oa-on-accent)]">
                            {session.unread_count}
                          </span>
                        ) : null}
                      </span>
                      <span className="mt-1 flex">{statusTag(session.status)}</span>
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
};
